/**
 * Universal Logger for AssetCAST Frontend
 * Uses Winston on server-side, console on client-side
 */

import { isDevelopment } from '@/config/app';

// Define log levels enum
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  HTTP = 3,
  DEBUG = 4,
}

/**
 * Logger interface for consistent API across server and client
 */
interface ILogger {
  debug(message: string, context?: string, meta?: any): void;
  info(message: string, context?: string, meta?: any): void;
  warn(message: string, context?: string, meta?: any): void;
  error(message: string, context?: string, error?: any): void;
  http(message: string, context?: string, meta?: any): void;
}

/**
 * Browser-safe logger implementation
 * Uses console with structured formatting
 */
class BrowserLogger implements ILogger {
  private minLevel: LogLevel = isDevelopment() ? LogLevel.DEBUG : LogLevel.INFO;

  private shouldLog(level: LogLevel): boolean {
    return level <= this.minLevel;
  }

  private formatMessage(level: string, message: string, context?: string): string {
    const timestamp = new Date().toISOString();
    const contextStr = context ? `[${context}]` : '';
    return `${timestamp} ${level} ${contextStr} ${message}`;
  }

  debug(message: string, context?: string, meta?: any): void {
    if (!this.shouldLog(LogLevel.DEBUG)) return;
    const formatted = this.formatMessage('DEBUG', message, context);
    if (meta) {
      console.debug(formatted, meta);
    } else {
      console.debug(formatted);
    }
  }

  info(message: string, context?: string, meta?: any): void {
    if (!this.shouldLog(LogLevel.INFO)) return;
    const formatted = this.formatMessage('INFO', message, context);
    if (meta) {
      console.info(formatted, meta);
    } else {
      console.info(formatted);
    }
  }

  warn(message: string, context?: string, meta?: any): void {
    if (!this.shouldLog(LogLevel.WARN)) return;
    const formatted = this.formatMessage('WARN', message, context);
    if (meta) {
      console.warn(formatted, meta);
    } else {
      console.warn(formatted);
    }
  }

  error(message: string, context?: string, error?: any): void {
    if (!this.shouldLog(LogLevel.ERROR)) return;
    const formatted = this.formatMessage('ERROR', message, context);

    if (error instanceof Error) {
      console.error(formatted, {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });
    } else if (error) {
      console.error(formatted, error);
    } else {
      console.error(formatted);
    }

    // In production, you could send errors to error tracking service here
    // if (!this.isDevelopment) {
    //   this.sendToErrorTracking({ message, context, error });
    // }
  }

  http(message: string, context?: string, meta?: any): void {
    if (!this.shouldLog(LogLevel.HTTP)) return;
    const formatted = this.formatMessage('HTTP', message, context);
    if (meta) {
      console.log(formatted, meta);
    } else {
      console.log(formatted);
    }
  }
}

/**
 * Server-side Winston logger implementation
 * Only imported and used on server-side
 */
class ServerLogger implements ILogger {
  private winstonLogger: any;

  constructor() {
    // Dynamically import winston only on server-side
    if (typeof window === 'undefined') {
      try {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const winston = require('winston');

        const levels = {
          error: 0,
          warn: 1,
          info: 2,
          http: 3,
          debug: 4,
        };

        const colors = {
          error: 'red',
          warn: 'yellow',
          info: 'green',
          http: 'magenta',
          debug: 'blue',
        };

        winston.addColors(colors);

        const level = isDevelopment() ? 'debug' : 'warn';

        const format = winston.format.combine(
          winston.format.timestamp({ format: 'YYYY-MM-DD HH:mm:ss:ms' }),
          winston.format.colorize({ all: true }),
          winston.format.printf((info: any) => {
            const { timestamp, level, message, context, ...meta } = info;
            const contextStr = context ? `[${context}]` : '';
            const metaStr = Object.keys(meta).length ? JSON.stringify(meta, null, 2) : '';
            return `${timestamp} ${level} ${contextStr} ${message} ${metaStr}`;
          })
        );

        this.winstonLogger = winston.createLogger({
          level,
          levels,
          format,
          transports: [new winston.transports.Console()],
        });
      } catch (err) {
        console.error('Failed to initialize Winston logger, falling back to console', err);
        this.winstonLogger = null;
      }
    }
  }

  debug(message: string, context?: string, meta?: any): void {
    if (this.winstonLogger) {
      this.winstonLogger.debug(message, { context, ...meta });
    } else {
      console.debug(`[${context || 'DEBUG'}]`, message, meta);
    }
  }

  info(message: string, context?: string, meta?: any): void {
    if (this.winstonLogger) {
      this.winstonLogger.info(message, { context, ...meta });
    } else {
      console.info(`[${context || 'INFO'}]`, message, meta);
    }
  }

  warn(message: string, context?: string, meta?: any): void {
    if (this.winstonLogger) {
      this.winstonLogger.warn(message, { context, ...meta });
    } else {
      console.warn(`[${context || 'WARN'}]`, message, meta);
    }
  }

  error(message: string, context?: string, error?: any): void {
    const meta: any = {};

    if (error instanceof Error) {
      meta.error = {
        message: error.message,
        stack: error.stack,
        name: error.name,
      };
    } else if (error) {
      meta.error = error;
    }

    if (this.winstonLogger) {
      this.winstonLogger.error(message, { context, ...meta });
    } else {
      console.error(`[${context || 'ERROR'}]`, message, meta);
    }
  }

  http(message: string, context?: string, meta?: any): void {
    if (this.winstonLogger) {
      this.winstonLogger.http(message, { context, ...meta });
    } else {
      console.log(`[${context || 'HTTP'}]`, message, meta);
    }
  }
}

function createLogger(): ILogger {
  // Check if we're in a browser environment
  if (typeof window !== 'undefined') {
    return new BrowserLogger();
  }

  // Server-side: use Winston
  return new ServerLogger();
}

// Export singleton instance
export const logger = createLogger();
